import { ActionBar, Checkbox, SpinnerWithBackground, Text, TextArea } from '@viapeople/components'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { PecRatingScaleEditor } from '../components/PecRatingScaleEditor'
import { DEFAULT_COLORS } from '../constants'
import { usePecStore } from '../store'
import type { PecConfigurationTabInput } from './types'

/**
 * @fileoverview PEC Configuration Tab
 *
 * Customize prompt text, rating scale, N/A option, and text feedback toggle
 */

/**
 * Generate default rating options for a given scale
 */
function generateDefaultOptions(count: number) {
	return Array.from({ length: count }, (_, i) => ({
		value: String(i + 1),
		valueLabel: String(i + 1),
		labelColor: DEFAULT_COLORS[i] || '#2563EB',
	}))
}

export const PecConfigurationTab = () => {
	const { organizationId: _organizationId } = useParams<{
		organizationId: string
		pecId: string
	}>()
	const navigate = useNavigate()
	const { pecCycle, useEditPecCycle } = usePecStore()
	const { editPecCycle, isLoading } = useEditPecCycle(pecCycle?.id)

	const {
		handleSubmit,
		control,
		formState: { errors },
		reset,
		watch,
		setValue,
	} = useForm<PecConfigurationTabInput>({
		defaultValues: {
			promptText: pecCycle?.promptText || '',
			ratingScale: pecCycle?.ratingScale || 5,
			ratingConfig: pecCycle?.ratingConfig || null,
			allowNa: pecCycle?.allowNa ?? false,
			textFeedbackEnabled: pecCycle?.textFeedbackEnabled ?? true,
		},
	})

	const ratingConfig = watch('ratingConfig')
	const ratingScale = watch('ratingScale')

	useEffect(() => {
		if (pecCycle) {
			reset({
				promptText: pecCycle.promptText,
				ratingScale: pecCycle.ratingScale,
				ratingConfig: pecCycle.ratingConfig,
				allowNa: pecCycle.allowNa ?? false,
				textFeedbackEnabled: pecCycle.textFeedbackEnabled ?? true,
			})
		}
	}, [pecCycle, reset])

	const handleSubmitInternal = async (data: PecConfigurationTabInput) => {
		// If ratingConfig is null build it from ratingScale so we never persist null when we have a scale
		const ratingConfig = data.ratingConfig ?? {
			count: data.ratingScale ?? 5,
			options: generateDefaultOptions(data.ratingScale ?? 5),
		}
		await editPecCycle({
			...data,
			ratingConfig,
			ratingScale: ratingConfig.count,
		})
	}

	// Derive current count and options from ratingConfig or ratingScale
	const currentCount = ratingConfig?.count || ratingScale || 5
	const currentOptions = ratingConfig?.options || generateDefaultOptions(currentCount)
	const currentAllowNa = watch('allowNa')

	const handleRatingConfigChange = (config: {
		count: number
		options: { value: string; valueLabel: string; labelColor: string }[]
		allowNa: boolean
	}) => {
		setValue('ratingConfig', {
			count: config.count,
			options: config.options,
		})
		setValue('ratingScale', config.count)
		setValue('allowNa', config.allowNa)
	}

	return (
		<form onSubmit={handleSubmit(handleSubmitInternal)} className='relative pb-12 mt-6 w-full'>
			{isLoading && <SpinnerWithBackground />}
			<div className='grid grid-cols-1 gap-6 px-6 max-w-4xl'>
				<div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
					<Text variant='small' weight='semibold' className='text-blue-900'>
						PEC Survey Format
					</Text>
					<Text variant='xsmall' className='text-blue-700 mt-2'>
						Participants will rate each person in the population based on your prompt text. Configure the rating scale
						and optional features below.
					</Text>
				</div>

				<Controller
					name='promptText'
					control={control}
					rules={{
						required: 'Prompt text is required',
						minLength: { value: 10, message: 'Prompt should be at least 10 characters' },
					}}
					render={({ field }) => (
						<TextArea
							label='Prompt Text'
							placeholder='On a scale of 1-5, how well does this person demonstrate the following:'
							variant={errors.promptText ? 'error' : 'default'}
							hint={errors.promptText?.message as string}
							value={field.value}
							onChange={field.onChange}
							onBlur={field.onBlur}
							rows={3}
							required
						/>
					)}
				/>

				{/* Rating Scale Editor */}
				<div className='space-y-4 p-6 bg-white border border-neutral-200 rounded-lg'>
					<PecRatingScaleEditor
						count={currentCount}
						options={currentOptions}
						allowNa={currentAllowNa}
						onChange={handleRatingConfigChange}
						disabled={pecCycle?.status !== 'draft'}
					/>
				</div>

				{/* Text Feedback Toggle */}
				<div className='space-y-4 p-6 bg-white border border-neutral-200 rounded-lg'>
					<div className='flex items-start gap-3'>
						<Controller
							name='textFeedbackEnabled'
							control={control}
							render={({ field }) => (
								<Checkbox
									id='textFeedbackEnabled'
									checked={field.value}
									onChange={(e) => field.onChange(e.target.checked)}
									disabled={pecCycle?.status !== 'draft'}
								/>
							)}
						/>
						<div className='flex-1'>
							<label htmlFor='textFeedbackEnabled'>
								<Text variant='small' weight='semibold'>
									Enable Optional Text Feedback
								</Text>
								<Text variant='xsmall' className='text-neutral-600 mt-1'>
									When enabled, participants can provide additional text feedback for each person they rate. This
									feedback is optional.
								</Text>
							</label>
						</div>
					</div>
				</div>
			</div>
			<ActionBar
				rightActions={[
					{
						variant: 'secondary' as const,
						label: 'Cancel',
						onClick: () => navigate('/pec'),
					},
					{
						variant: 'primary' as const,
						label: 'Save',
						onClick: handleSubmit(handleSubmitInternal),
					},
				]}
			/>
		</form>
	)
}
